import React, { useState } from 'react'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { useIntl } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_COLOR_ON_DARK, BASIC_BUTTON_TYPE_FLAT } from '@divvy-web/skylab.basicbutton'
import { FormTextInput } from '@divvy-web/skylab.form'
import { useFormContext, useWatch } from '@divvy-web/skylab.react-hook-form'

import styles from './styles.module.scss'

type FormTextInputProps = React.ComponentProps<typeof FormTextInput>
export interface MaskedFormTextInputProps extends FormTextInputProps {
  maskedValue: (value: string) => string
  unmaskedValue: (value: string) => string
  sanitize?: (value: string) => string
}

const isValueMasked = (value: string) => {
  const maskRegex = new RegExp(/\*+/)
  return maskRegex.test(value)
}

const MaskedFormTextInput = ({ maskedValue, unmaskedValue, sanitize, ...rest }: MaskedFormTextInputProps) => {
  const { formatMessage } = useIntl()
  const { name, onBlur, onChange, onFocus } = rest

  const [getClassName] = useNameFormatter('MaskedFormTextInput')
  const [isMasked, setIsMasked] = useState(true)

  const displayFieldName = `${name}.display`
  const valueFieldName = `${name}.value`

  const { control, setValue } = useFormContext()

  const [value, displayValue] = useWatch({ control, name: [valueFieldName, displayFieldName] })
  const handleRightIconClick = () => {
    setIsMasked((prev) => {
      if (displayValue?.length) {
        setValue(displayFieldName, !prev ? maskedValue(displayValue) : unmaskedValue(value), {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        })
      }

      return !prev
    })
  }

  const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange?.(e)
    const { value } = e.target

    setIsMasked(false)
    setValue(displayFieldName, isValueMasked(value) ? '' : unmaskedValue(value), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    })
    setValue(valueFieldName, isValueMasked(value) ? '' : sanitize?.(value) ?? value, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    })
  }

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    onBlur?.(e)
    if (value) {
      setIsMasked(true)
      setValue(displayFieldName, maskedValue(value), {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      })
    }
  }

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
    onFocus?.(e)
    if (value) {
      setIsMasked(false)
      setValue(displayFieldName, unmaskedValue(value), { shouldDirty: true, shouldValidate: true, shouldTouch: true })
    }
  }

  const rightIconButton = (
    <BasicButton
      color={BASIC_BUTTON_COLOR_ON_DARK}
      disabled={!value}
      icon={isMasked ? 'eyeClosed' : 'eye'}
      name={
        isMasked
          ? formatMessage({
              defaultMessage: 'unmask input',
              id: 'sputnik.MaskedFormTextInput__/w6TLV',
            })
          : formatMessage({
              defaultMessage: 'mask input',
              id: 'sputnik.MaskedFormTextInput__ZY3CkP',
            })
      }
      type={BASIC_BUTTON_TYPE_FLAT}
      onClick={handleRightIconClick}
    />
  )

  return (
    <FormTextInput
      {...rest}
      className={`${styles[getClassName('')]} fs-mask`}
      name={displayFieldName}
      rightIcon={rightIconButton}
      onBlur={handleBlur}
      onChange={handleOnChange}
      onFocus={handleFocus}
    />
  )
}

export default MaskedFormTextInput
